import { useEffect, useRef } from 'react';

interface WaveOptions {
  amp?: number;
  grid?: boolean;
  idle?: boolean;
  autostart?: boolean;
}

interface WaveCtrl {
  start: () => void;
  stop:  () => void;
}

export function useWaveform(opts: WaveOptions = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctrlRef   = useRef<WaveCtrl | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cvs: HTMLCanvasElement       = canvas;
    const render: CanvasRenderingContext2D = ctx;

    const DPR    = Math.min(window.devicePixelRatio ?? 1, 2);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0;
    let t = Math.random() * 10;
    let env = 0.5, target = 0.6, timer = 0;
    let running = opts.autostart !== false;
    let rafId   = 0;

    function resize() {
      const r = cvs.getBoundingClientRect();
      W = r.width; H = r.height;
      cvs.width  = W * DPR;
      cvs.height = H * DPR;
      render.setTransform(DPR, 0, 0, DPR, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    const drawGrid = opts.grid !== false;

    function frame() {
      render.clearRect(0, 0, W, H);

      if (drawGrid) {
        render.strokeStyle = 'rgba(242,236,226,0.05)'; render.lineWidth = 1;
        for (let gx = 0; gx <= W; gx += 28) {
          render.beginPath(); render.moveTo(gx + 0.5, 0); render.lineTo(gx + 0.5, H); render.stroke();
        }
        render.strokeStyle = 'rgba(242,236,226,0.08)';
        render.beginPath(); render.moveTo(0, H / 2 + 0.5); render.lineTo(W, H / 2 + 0.5); render.stroke();
      }

      const mid = H / 2;
      const amp = H * (opts.amp ?? 0.32) * env;
      const grad = render.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0,   'rgba(180,83,9,0.12)');
      grad.addColorStop(0.5, '#E2A56B');
      grad.addColorStop(1,   'rgba(180,83,9,0.12)');

      render.strokeStyle  = grad;
      render.lineWidth    = 2;
      render.shadowColor  = 'rgba(226,165,107,0.45)';
      render.shadowBlur   = 9;
      render.beginPath();

      for (let x = 0; x <= W; x += 3) {
        const p   = x / W;
        const win = Math.sin(p * Math.PI);
        const s   = Math.sin(p * 24 + t * 2.4) * 0.6
                  + Math.sin(p * 44 - t * 1.7) * 0.28
                  + Math.sin(p * 10 + t * 1.1) * 0.5;
        const jit = Math.sin(p * 140 + t * 9) * 0.13 * env;
        const y   = mid + (s + jit) * amp * win;
        x === 0 ? render.moveTo(x, y) : render.lineTo(x, y);
      }

      render.stroke(); render.shadowBlur = 0;

      if (running && !reduce) {
        t += 0.016; timer -= 1;
        if (timer <= 0) {
          timer  = 36 + Math.random() * 64;
          target = (opts.idle ? 0.18 : 0.28) + Math.random() * (opts.idle ? 0.25 : 0.8);
        }
        env += (target - env) * 0.06;
      } else {
        env += (0.5 - env) * 0.1;
      }

      rafId = requestAnimationFrame(frame);
    }

    frame();

    ctrlRef.current = {
      start: () => { running = true; },
      stop:  () => { running = false; target = 0.5; },
    };

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      ctrlRef.current = null;
    };
  // opts are static per instantiation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { canvasRef, ctrlRef };
}
